import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, User, Users, Pencil } from 'lucide-react';
import type { Technician } from '@shared/types';
import { EditTechnicianModal } from './TechnicianManager/EditTechnicianModal';
import { getApiUrl } from '@/config';

const COLORS = [
    '#f472b6', '#22d3ee', '#a78bfa', '#fbbf24', '#34d399', '#f87171', '#60a5fa', '#c084fc'
];

interface TechnicianManagerProps {
    technicians: Technician[];
    setTechnicians: React.Dispatch<React.SetStateAction<Technician[]>>;
}

export const TechnicianManager: React.FC<TechnicianManagerProps> = ({ technicians, setTechnicians }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Add New State
    const [newName, setNewName] = useState('');
    const [newService, setNewService] = useState('Maintenance');
    const [error, setError] = useState('');
    const [showArchived, setShowArchived] = useState(false);

    // Edit Modal State
    const [editingTechnician, setEditingTechnician] = useState<Technician | null>(null);

    const handleAdd = async () => {
        if (!newName.trim()) return;
        setError('');

        const color = COLORS[technicians.length % COLORS.length];
        const newTechId = crypto.randomUUID();

        try {
            const token = localStorage.getItem('token');
            
            // Create Technician Profile only
            const newTechnician: Technician = {
                id: newTechId,
                name: newName,
                service: newService,
                color,
                assignments: [],
                overrides: {},
                archived: false
            };

            // Call API to save technician
            const res = await fetch(getApiUrl('/api/technicians'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify([...technicians, newTechnician])
            });

            if (!res.ok) {
                throw new Error('Erreur lors de la sauvegarde du technicien');
            }

            setTechnicians([...technicians, newTechnician]);

            // Reset
            setNewName('');
            setNewService('Maintenance');
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleUpdateTechnician = (id: string, updates: Partial<Technician>) => {
        setTechnicians(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
        // If we are updating the currently edited tech, update the modal state too
        if (editingTechnician && editingTechnician.id === id) {
            setEditingTechnician(prev => prev ? { ...prev, ...updates } : null);
        }
    };

    const archiveTechnician = (id: string) => {
        if (confirm('Êtes-vous sûr de vouloir archiver ce technicien ? Il ne sera plus visible sur le planning mais son historique sera conservé.')) {
            handleUpdateTechnician(id, { archived: true });
        }
    };

    const unarchiveTechnician = (id: string) => {
        if (confirm('Réactiver ce technicien ?')) {
            handleUpdateTechnician(id, { archived: false });
        }
    };

    // Fetch archived technicians if needed
    React.useEffect(() => {
        if (showArchived) {
            const fetchArchived = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(getApiUrl('/api/technicians?includeArchived=true'), {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const allTechs = await res.json();
                        // Merge with existing state (to avoid losing local changes if any)
                        // But usually state is already synchronized with server
                        setTechnicians(allTechs);
                    }
                } catch (err) {
                    console.error('Failed to fetch archived technicians', err);
                }
            };
            fetchArchived();
        }
    }, [showArchived, setTechnicians]);

    return (
        <div className="glass-panel p-6 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Users size={24} className="text-primary" />
                    Équipe ({technicians.filter(t => !t.archived).length}) {showArchived && <span className="text-amber-500 font-normal text-sm">(+ {technicians.filter(t => t.archived).length} archivés)</span>}
                </h2>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="glass-button text-sm flex-1 sm:flex-none justify-center"
                    >
                        {isOpen ? 'Masquer' : 'Gérer l\'équipe'}
                    </button>
                    <button
                        onClick={() => setShowArchived(!showArchived)}
                        className={`glass-button text-sm flex-1 sm:flex-none justify-center ${showArchived ? 'bg-amber-500/20 text-amber-400' : ''}`}
                    >
                        {showArchived ? 'Masquer les archivés' : 'Voir les archivés'}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                    >
                        {/* Add New Form */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-6 bg-white/5 p-4 rounded-xl border border-white/10">
                            <div className="md:col-span-1">
                                <label className="block text-sm text-gray-400 mb-1">Nom du technicien</label>
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="Ex: Jean Dupont"
                                    className="glass-input w-full"
                                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                                />
                            </div>

                            <div className="md:col-span-1">
                                <label className="block text-sm text-gray-400 mb-1">Service</label>
                                <input
                                    type="text"
                                    value={newService}
                                    onChange={(e) => setNewService(e.target.value)}
                                    className="glass-input w-full"
                                    placeholder="Service"
                                />
                            </div>

                            <div className="flex gap-2 md:col-span-1 justify-end">
                                <button
                                    onClick={handleAdd}
                                    className="glass-button bg-primary/20 text-primary border-primary/50 flex items-center gap-2 w-full md:w-auto justify-center"
                                >
                                    <Plus size={18} /> Ajouter au Planning
                                </button>
                            </div>

                            {error && (
                                <div className="md:col-span-3 text-red-400 text-sm bg-red-500/10 p-2 rounded">
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {technicians
                                .filter(tech => showArchived ? tech.archived : !tech.archived)
                                .map((tech) => (
                                <motion.div
                                    key={tech.id}
                                    layout
                                    initial={{ scale: 0.95, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className={`flex items-center justify-between p-3 rounded-xl border transition-colors hover:bg-white/10 ${tech.archived ? 'bg-black/40 border-white/5 opacity-60' : 'bg-white/5 border-white/10'}`}
                                    style={{ borderLeft: `4px solid ${tech.archived ? '#6b7280' : tech.color} ` }}
                                >
                                    <div className="flex items-center gap-3 overflow-hidden cursor-pointer" onClick={() => !tech.archived && setEditingTechnician(tech)}>
                                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                                            <User size={16} style={{ color: tech.archived ? '#6b7280' : tech.color }} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-medium flex items-center gap-2 truncate">
                                                {tech.name} {tech.archived && <span className="text-[10px] bg-amber-500/20 text-amber-500 px-1.5 py-0.5 rounded">Archivé</span>}
                                            </div>
                                            <div className="text-xs text-gray-400 truncate">
                                                {tech.service}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        {!tech.archived ? (
                                            <>
                                                <button
                                                    onClick={() => setEditingTechnician(tech)}
                                                    className="p-2 hover:bg-white/20 text-gray-400 hover:text-white rounded-lg transition-colors"
                                                    title="Modifier"
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                                <button
                                                    onClick={() => archiveTechnician(tech.id)}
                                                    className="p-2 hover:bg-amber-500/20 text-gray-400 hover:text-amber-400 rounded-lg transition-colors"
                                                    title="Archiver"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => unarchiveTechnician(tech.id)}
                                                className="p-2 hover:bg-green-500/20 text-gray-400 hover:text-green-400 rounded-lg transition-colors"
                                                title="Réactiver"
                                            >
                                                <Plus size={16} />
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {editingTechnician && (
                <EditTechnicianModal
                    technician={editingTechnician}
                    isOpen={true}
                    onClose={() => setEditingTechnician(null)}
                    onSave={handleUpdateTechnician}
                />
            )}
        </div>
    );
};
